/**
 * playbackClock.ts
 * موضع التشغيل **المستمرّ** على الشريط الزمني — قناة مستقلّة تماماً عن React/zustand.
 *
 * لماذا موجود أصلاً؟
 * ------------------
 * كان `frameFraction` يسكن في متجر zustand وحده، فكل تغيير فيه = إعادة رسم React لشجرة
 * الخريطة كاملة. ولأن 60 إعادة رسم في الثانية مستحيلة، كُمِّم الكسر إلى ستّ درجات فقط
 * (`subSteps = 6` في useAutoplay): 0, 0.166, 0.333, 0.5, 0.666, 0.833. أي أن اللون كان
 * يقفز **17% دفعة واحدة كل 500 مللي** بدل أن ينساب.
 *
 * وهذا مقيس لا مفترَض: قياس فرق الإطارات (scene score) على تسجيل من نظامنا أعطى خطّاً
 * قاعدياً ~0.001 (الجسيمات وحدها تتحرّك، الألوان ساكنة) تتخلّله قفزات 0.03–0.08 — بينما
 * نفس القياس على Zoom Earth يعطي نطاقاً متجانساً 0.002–0.008 **بلا قفزة واحدة**. ذلك
 * الفرق هو كامل ما يُقرأ بالعين كـ«تقطيع».
 *
 * الحلّ ليس تسريع React بل تجاوزه: الشيدر (`u_blend` في RasterHeatmapGLLayer) يقبل أي
 * قيمة عشرية ويمزج على الـ GPU بلا تكلفة إضافية تُذكر، و`setBlend()` يستدعي
 * `triggerRepaint()` مباشرة. فالقيمة المستمرّة تسير من هنا إلى الطبقات عبر اشتراك خفيف،
 * ويبقى المتجر يستقبل الفهرس/الكسر بالوتيرة المنخفضة القديمة لأجل الواجهة (تسميات
 * الشريط، محور القمر) — أي React يعيد الرسم ~3 مرّات في الثانية بدل 120.
 *
 * ملاحظة تصميمية مهمّة: المشتركون يُستدعون **متزامناً** داخل نبضة rAF الخاصة بالتشغيل،
 * ولا يفعلون شيئاً سوى ضبط uniform وطلب إعادة رسم. لا تضع هنا أي عمل ثقيل.
 */

export interface PlaybackPosition {
    /** فهرس الإطار على المحور الفعّال. */
    index: number;
    /** الكسر داخل الإطار 0..1 — **عشري كامل**، لا مكمَّم. */
    fraction: number;
}

let position: PlaybackPosition = { index: 0, fraction: 0 };
const subscribers = new Set<(p: PlaybackPosition) => void>();

export function getPlaybackPosition(): PlaybackPosition {
    return position;
}

/**
 * يكتب الموضع المستمرّ ويُخطر المشتركين فوراً (متزامناً).
 * يُستدعى من useAutoplay كل نبضة رسم أثناء التشغيل، ومن مقبض الشريط أثناء السحب.
 */
export function setPlaybackPosition(index: number, fraction: number): void {
    if (position.index === index && position.fraction === fraction) return;
    position = { index, fraction };
    for (const fn of subscribers) {
        try { fn(position); } catch { /* مشترك واحد لا يُسقط البقية */ }
    }
}

/** اشتراك يُستدعى فوراً بالقيمة الحالية ثم عند كل تغيّر. يُعيد دالة إلغاء. */
export function subscribePlayback(fn: (p: PlaybackPosition) => void): () => void {
    subscribers.add(fn);
    fn(position);
    return () => { subscribers.delete(fn); };
}

/**
 * نسبة المزج الصحيحة لطبقة تعرض حالياً الإطار `frameIndex`.
 *
 * الفائدة الحقيقية هنا هي **ردم فجوة الإطار الواحد**: الساعة تتقدّم داخل نبضة rAF، بينما
 * روابط `url`/`urlNext` للطبقة تأتي من حالة React ولا تتحدّث إلا في الرسم التالي. خلال
 * تلك الفجوة يكون `p.index` قد صار `frameIndex + 1`؛ لو أعدنا `p.fraction` (≈0) لعادت
 * الطبقة لحظةً إلى الإطار القديم — وميض للخلف. بإرجاع 1 تبقى معروضةً على الإطار التالي
 * وهو بالضبط ما سيصير «الحالي» بعد إعادة الرسم، فيمرّ الانتقال بلا أي قفزة.
 */
export function blendFor(p: PlaybackPosition, frameIndex: number): number {
    if (p.index === frameIndex) return p.fraction;
    return p.index > frameIndex ? 1 : 0;
}
